/**
 * moyaapp-landing.js  (FIXED — logged-in tag gap)
 * -----------------------------------------------
 * Client-side logic for the /pages/moyaapp landing page.
 *
 * Flow:
 * 1. Read ?token= from URL → persist (localStorage + sessionStorage)
 * 2. Call Worker /balance to get voucher count
 * 3a. If logged in  → call Worker /tag-customer (tag + voucher counter +
 *      session-token metafield + phone + consent), THEN reveal "Start Playing".
 * 3b. If not logged in → show phone + consent fields + login/register buttons.
 *
 * --- WHAT CHANGED IN THIS VERSION (and why) ---------------------------------
 * BUG: a pre-logged-in customer could reach this page WITHOUT a token in the
 * URL (e.g. opened from inside MoyaApp via a link that didn't carry ?token=,
 * or in a fresh in-app webview session where sessionStorage was empty). The old
 * code hit `if (!token) { showError(); return; }` and bailed BEFORE tagging —
 * but the Liquid had already rendered a working "Start Playing" button (gated
 * only on `{% if customer %}`). The customer clicked through to the shop with
 * NO moyaapp tag, NO session-token metafield and NO voucher counter.
 *
 * FIXES:
 *  (A) Token now persists in localStorage as well as sessionStorage, so a token
 *      seen on ANY visit in this webview survives session/tab boundaries that
 *      in-app browsers frequently reset. getToken() checks URL → localStorage →
 *      sessionStorage in that order.
 *  (B) "Start Playing" is NO LONGER revealed for a logged-in customer unless
 *      tagging actually succeeded. The CTA is only shown by revealCta() inside
 *      the success path. A logged-in customer can no longer slip into the shop
 *      untagged.
 *  (C) When a logged-in customer has no usable token (can't be tagged) or
 *      tagging fails, we show an explicit recovery state with a Retry button
 *      instead of silently letting them proceed.
 *
 * The Cloudflare Worker /tag-customer endpoint is UNCHANGED — it already sets
 * the tag, voucher counter and moya_session_token metafield. The only bug was
 * that it was never being called for these customers.
 */

(function () {
  'use strict';

  const cfg = window.MoyaConfig || {};

  const STORAGE_KEY_TOKEN      = 'moya_session_token';
  const STORAGE_KEY_VOUCHERS   = 'moya_voucher_count';
  const STORAGE_KEY_UTM        = 'moya_utm_params';
  const STORAGE_KEY_PHONE      = 'moya_pre_auth_phone';
  const STORAGE_KEY_CONSENT    = 'moya_pre_auth_consent';
  const STORAGE_KEY_WORKER_URL = 'moya_worker_url';

  // UTM keys we capture and preserve through the login flow
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  const els = {
    loading:     document.getElementById('moya-loading'),
    actions:     document.getElementById('moya-actions'),
    status:      document.getElementById('moya-status'),
    voucherBox:  document.getElementById('moya-voucher-box'),
    voucherCount:document.getElementById('moya-voucher-count'),
    title:       document.getElementById('moya-title'),
    subtitle:    document.getElementById('moya-subtitle'),
    loginBtn:    document.getElementById('moya-login-btn'),
    registerBtn: document.getElementById('moya-register-btn'),
    cta:         document.getElementById('moya-cta'),
    phoneInput:  document.getElementById('moya-phone'),
    consentBox:  document.getElementById('moya-consent'),
  };

  // -------------------------------------------------------------------------
  // INIT
  // -------------------------------------------------------------------------
  async function init() {
    const token = getToken();

    // FIX (B/C): a missing token must be handled differently depending on auth
    // state. A logged-in customer with no token previously got a working
    // "Start Playing" button and proceeded untagged — that is the bug. Now we
    // show a recovery state and do NOT reveal the CTA.
    if (!token) {
      if (cfg.isLoggedIn) {
        showRecovery(
          'We couldn\'t find your MoyaApp session, so your free tickets aren\'t linked yet. ' +
          'Please head back to the MoyaApp app and tap the Dream Drive link again.'
        );
      } else {
        showError('No MoyaApp session token found. Please use the link provided by MoyaApp.');
      }
      hideLoading();
      return;
    }

    // FIX (A): persist token in BOTH localStorage (survives webview session
    // resets) and sessionStorage (back-compat with the rest of the flow).
    persistToken(token);
    if (cfg.workerUrl) {
      try { localStorage.setItem(STORAGE_KEY_WORKER_URL, cfg.workerUrl); } catch (e) {}
      sessionStorage.setItem(STORAGE_KEY_WORKER_URL, cfg.workerUrl);
    }

    // Capture UTM params from the current URL and persist them so they survive
    // the login redirect (logged-out users are sent to /account/login and back).
    captureUtmParams();

    // Wire up button clicks to capture phone + consent before redirect
    bindPreAuthCapture();

    // Update login/register links to carry the token + UTMs back after redirect
    appendTokenToAuthLinks(token);

    // Fetch balance from Worker
    let vouchers = 0;
    let balanceFailed = false;
    try {
      vouchers = await fetchBalance(token);
    } catch (err) {
      balanceFailed = true;
      // Logged-out: show the error and stop — nothing more we can do without a balance.
      // Logged-in: fall through with vouchers = 0 so tagCustomer still fires.
      // Customer tagging (tag, counter, token, phone, consent) is independent of balance.
      if (!cfg.isLoggedIn) {
        showError('Could not retrieve your voucher balance. Please try again or contact support.');
        hideLoading();
        showActions();
        return;
      }
    }

    // Store voucher count for use at checkout
    sessionStorage.setItem(STORAGE_KEY_VOUCHERS, String(vouchers));

    // Show voucher count (skip if balance failed — box would show 0 which is misleading)
    if (!balanceFailed) showVoucherBox(vouchers);

    if (cfg.isLoggedIn) {
      await handleLoggedInUser(token, vouchers);
    } else {
      handleLoggedOutUser(vouchers);
    }
  }

  // -------------------------------------------------------------------------
  // BALANCE FETCH
  // -------------------------------------------------------------------------
  async function fetchBalance(token) {
    if (!cfg.workerUrl) {
      throw new Error('Worker URL not configured');
    }

    const res = await fetch(`${cfg.workerUrl}/balance?token=${encodeURIComponent(token)}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Balance fetch failed: ${res.status}`);
    }

    const data = await res.json();
    return Number(data.vouchers ?? 0);
  }

  // -------------------------------------------------------------------------
  // LOGGED IN FLOW
  // Tags customer as 'moyaapp', sets the voucher counter + session-token
  // metafield, and applies any stored phone + marketing consent.
  // FIX (B): "Start Playing" is revealed ONLY after tagging succeeds.
  // FIX (C): on tag failure we show a Retry recovery state — never a silent
  // untagged entry into the shop.
  // -------------------------------------------------------------------------
  async function handleLoggedInUser(token, vouchers) {
    setStatus('Syncing your vouchers with Dream Drive...');

    try {
      await tagCustomer(token, vouchers);

      // Pre-auth data has been applied — no longer needed
      sessionStorage.removeItem(STORAGE_KEY_PHONE);
      sessionStorage.removeItem(STORAGE_KEY_CONSENT);

      setStatus('✓ Vouchers synced! Redirecting...', 'success');

      // Tagging confirmed — safe to send them into the shop.
      setTimeout(() => {
        window.location.href = cfg.shopLink || '/collections/all';
      }, 1500);

    } catch (err) {
      console.error('Tag customer error:', err);
      // FIX (C): do NOT reveal "Start Playing" here. Tagging failed, so letting
      // them proceed would recreate the exact bug we are fixing. Offer a retry.
      hideLoading();
      showRecovery('We couldn\'t link your MoyaApp vouchers to your account just now.');
    }
  }

  // -------------------------------------------------------------------------
  // LOGGED OUT FLOW
  // Shows phone field, consent checkbox, and login/register buttons.
  // -------------------------------------------------------------------------
  function handleLoggedOutUser(vouchers) {
    hideLoading();

    if (vouchers > 0) {
      els.title && (els.title.textContent = `You have ${vouchers} free ticket${vouchers !== 1 ? 's' : ''} waiting`);
      els.subtitle && (els.subtitle.textContent = 'Log in or create a Dream Drive account to claim them.');
    }

    showActions();
  }

  // -------------------------------------------------------------------------
  // TAG CUSTOMER
  // Calls our Worker which uses the Shopify Admin API to:
  // - Add 'moyaapp' tag to customer
  // - Set custom.moyaapp_voucher_counter metafield
  // - Set custom.moya_session_token metafield
  // - Update phone number (if provided pre-login)
  // - Set email + SMS marketing consent (based on pre-login checkbox)
  // -------------------------------------------------------------------------
  async function tagCustomer(token, vouchers) {
    if (!cfg.workerUrl || !cfg.customerId) {
      // Defensive: if we somehow can't identify the customer, treat as a failure
      // so the caller shows the recovery state rather than a false success.
      throw new Error('Missing worker URL or customer ID');
    }

    // Retrieve phone + consent stored before the login redirect
    const phone           = sessionStorage.getItem(STORAGE_KEY_PHONE) || null;
    const consentStored   = sessionStorage.getItem(STORAGE_KEY_CONSENT);
    // Default true if nothing stored (checkbox is pre-checked; only stored as
    // 'false' if the user explicitly unticked before redirecting)
    const marketingConsent = consentStored !== 'false';

    const res = await fetch(`${cfg.workerUrl}/tag-customer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer_id:        cfg.customerId,
        token,
        vouchers,
        phone:              phone || null,
        marketing_consent:  marketingConsent,
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Tag customer failed: ${res.status}`);
    }

    return res.json();
  }

  // -------------------------------------------------------------------------
  // HELPERS
  // -------------------------------------------------------------------------

  /**
   * Read token from URL ?token=, then localStorage, then sessionStorage.
   * FIX (A): localStorage is checked before sessionStorage because in-app
   * webviews routinely start a fresh sessionStorage but keep localStorage,
   * so it is the more reliable carrier between MoyaApp page hops.
   */
  function getToken() {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('token');
    if (fromUrl) return fromUrl;

    let fromLocal = null;
    try { fromLocal = localStorage.getItem(STORAGE_KEY_TOKEN); } catch (e) {}
    if (fromLocal) return fromLocal;

    return sessionStorage.getItem(STORAGE_KEY_TOKEN) || null;
  }

  /** Persist the token to both stores (localStorage may throw in private mode). */
  function persistToken(token) {
    try { localStorage.setItem(STORAGE_KEY_TOKEN, token); } catch (e) {}
    sessionStorage.setItem(STORAGE_KEY_TOKEN, token);
  }

  /**
   * Attach click listeners to the login + register buttons.
   * Before the browser follows the link, we save the current phone value
   * and consent state to sessionStorage so they're available post-login.
   */
  function bindPreAuthCapture() {
    function captureAndProceed() {
      const phone   = els.phoneInput ? els.phoneInput.value.trim() : '';
      const consent = els.consentBox  ? els.consentBox.checked     : true;

      if (phone) {
        sessionStorage.setItem(STORAGE_KEY_PHONE, phone);
      } else {
        sessionStorage.removeItem(STORAGE_KEY_PHONE);
      }
      // Store consent as string; only a stored 'false' counts as opted-out so
      // the default (no stored value) is treated as opted-in.
      sessionStorage.setItem(STORAGE_KEY_CONSENT, String(consent));
      // Allow the anchor's href redirect to proceed normally
    }

    els.loginBtn    && els.loginBtn.addEventListener('click',    captureAndProceed);
    els.registerBtn && els.registerBtn.addEventListener('click', captureAndProceed);
  }

  /** Append token + any UTM params to login/register links so both survive the redirect */
  function appendTokenToAuthLinks(token) {
    const utmSuffix  = getStoredUtmString();
    const returnPath = '/pages/moyaapp?token=' + token + utmSuffix;

    if (els.loginBtn) {
      const base = els.loginBtn.href.split('?')[0];
      els.loginBtn.href = `${base}?checkout_url=${encodeURIComponent(returnPath)}`;
    }

    if (els.registerBtn) {
      // return_to sends Shopify back to the landing page post-registration,
      // which re-triggers tagCustomer with the stored phone + consent.
      els.registerBtn.href = `/account/register?return_to=${encodeURIComponent(returnPath)}`;
    }
  }

  /**
   * Read UTM params from the current URL and persist them in sessionStorage.
   * Falls back to any value already stored (covers the post-login return visit
   * where UTMs are no longer in the URL but the token is).
   */
  function captureUtmParams() {
    const params   = new URLSearchParams(window.location.search);
    const utmPairs = UTM_KEYS
      .filter(k => params.has(k))
      .map(k => `${k}=${encodeURIComponent(params.get(k))}`);

    if (utmPairs.length) {
      sessionStorage.setItem(STORAGE_KEY_UTM, utmPairs.join('&'));
    }
  }

  /** Returns stored UTM string prefixed with '&', or empty string if none */
  function getStoredUtmString() {
    const stored = sessionStorage.getItem(STORAGE_KEY_UTM);
    return stored ? '&' + stored : '';
  }

  function showVoucherBox(count) {
    if (!els.voucherBox || !els.voucherCount) return;
    els.voucherCount.textContent = count;
    els.voucherBox.style.display = 'block';
  }

  function hideLoading() {
    if (els.loading) els.loading.style.display = 'none';
  }

  function showActions() {
    if (els.actions) els.actions.style.display = 'flex';
  }

  /**
   * Reveal the "Start Playing" CTA. Called ONLY after tagging has succeeded,
   * so a logged-in customer can never reach the shop without being tagged.
   * (Kept as a helper so the success path is explicit and easy to audit.)
   */
  function revealCta() {
    showActions();
    if (els.cta) els.cta.style.display = 'block';
  }

  /**
   * FIX (C): explicit, non-silent recovery state for logged-in customers who
   * can't be tagged (no token, or tag call failed). Shows a message and a
   * Retry button that re-runs init — it does NOT reveal "Start Playing".
   */
  function showRecovery(message) {
    setStatus(message, 'error');

    if (!els.actions) return;

    // Hide any login/register/Start Playing controls so the only action is Retry
    Array.prototype.forEach.call(els.actions.children, function (child) {
      child.style.display = 'none';
    });

    // Avoid stacking multiple retry buttons on repeated calls
    let retry = document.getElementById('moya-retry-btn');
    if (!retry) {
      retry = document.createElement('button');
      retry.id = 'moya-retry-btn';
      retry.type = 'button';
      retry.className = 'moya-landing__btn moya-landing__btn--primary';
      retry.textContent = 'Retry';
      retry.addEventListener('click', function () {
        retry.disabled = true;
        retry.textContent = 'Checking...';
        // Re-run the whole flow; token (if any) is re-read from URL/storage.
        init().finally(function () {
          retry.disabled = false;
          retry.textContent = 'Retry';
        });
      });
      els.actions.appendChild(retry);
    } else {
      retry.style.display = 'block';
    }

    showActions();
  }

  function setStatus(msg, type) {
    if (!els.status) return;
    els.status.textContent = msg;
    els.status.className   = 'moya-landing__status' + (type ? ` moya-landing__status--${type}` : '');
  }

  function showError(msg) {
    setStatus(msg, 'error');
    if (els.loading) els.loading.style.display = 'none';
    showActions();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
